import { useState, useEffect, useMemo, useCallback } from 'react'
import { useLogto } from '@logto/react'
import { 
  DashboardConfig, 
  DashboardFilters, 
  DashboardAccessInfo, 
  DashboardStats 
} from '../types/dashboard'
import dashboardService from '../services/dashboardService'

/**
 * Custom hook for managing dashboard data and user access
 * Now uses secure API that only returns user-accessible dashboards
 */
export const useDashboards = () => {
  const { isAuthenticated } = useLogto()
  
  // State
  const [allDashboards, setAllDashboards] = useState<DashboardConfig[]>([])
  const [filters, setFilters] = useState<DashboardFilters>({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [searchSuggestions, setSearchSuggestions] = useState<DashboardConfig[]>([])

  // Load dashboards when authenticated
  useEffect(() => {
    const loadDashboards = async () => {
      if (!isAuthenticated) {
        setAllDashboards([])
        setIsLoading(false)
        return
      }

      try {
        setError(null)
        setIsLoading(true)
        
        // Fetch user-accessible dashboards from secure API
        const dashboards = await dashboardService.getAllDashboards()
        setAllDashboards(dashboards)
        
        if (dashboards.length === 0) {
          console.info('No accessible dashboards found for current user')
        }
      } catch (err) {
        console.error('Failed to load dashboards:', err)
        setError(err instanceof Error ? err : new Error('Failed to load dashboards'))
        setAllDashboards([])
      } finally {
        setIsLoading(false)
      }
    }

    loadDashboards()
  }, [isAuthenticated])

  // Filter dashboards based on current filters
  const filteredDashboards = useMemo(() => {
    return dashboardService.filterDashboards(filters, allDashboards)
  }, [filters, allDashboards])

  // Convert filtered dashboards to access info format (all have access since API pre-filtered)
  const dashboardsWithAccess = useMemo((): DashboardAccessInfo[] => {
    return filteredDashboards.map(dashboard => ({
      dashboard,
      hasAccess: true,
      missingRoles: []
    }))
  }, [filteredDashboards])

  // Get favorite dashboards
  const favoriteDashboards = useMemo(() => {
    return dashboardService.getFavoriteDashboards()
      .filter(dashboard => allDashboards.some(accessible => accessible.id === dashboard.id))
  }, [allDashboards])

  // Get recent dashboards
  const recentDashboards = useMemo(() => {
    return dashboardService.getRecentDashboards()
      .filter(dashboard => allDashboards.some(accessible => accessible.id === dashboard.id))
  }, [allDashboards])

  // Get dashboard statistics
  const dashboardStats = useMemo((): DashboardStats => {
    return dashboardService.getDashboardStats(allDashboards)
  }, [allDashboards])

  // Get filter options (from accessible dashboards only)
  const filterOptions = useMemo(() => {
    return {
      categories: [...new Set(allDashboards.map(d => d.category).filter(Boolean))] as string[],
      tags: [...new Set(allDashboards.flatMap(d => d.tags || []))]
      // Note: 'clients' removed since users should not see other clients
    }
  }, [allDashboards])

  // Update search suggestions based on current search term
  useEffect(() => {
    if (filters.search && filters.search.length > 1) {
      const suggestions = dashboardService.searchDashboards(filters.search, 5, allDashboards)
      setSearchSuggestions(suggestions)
    } else {
      setSearchSuggestions([])
    }
  }, [filters.search, allDashboards])

  // Helper function to get LogTo ID token from localStorage
  const getLogtoIdToken = useCallback(() => {
    // Check all LogTo keys in localStorage
    const allLogtoKeys = Object.keys(localStorage).filter(key => key.startsWith('logto'))
    
    // Look for keys that match the pattern logto:{clientId}:idToken
    const idTokenKeys = allLogtoKeys.filter(key => key.endsWith(':idToken'))
    
    // Find the ID token (should only be one)
    for (const key of idTokenKeys) {
      const value = localStorage.getItem(key)
      if (value && typeof value === 'string' && value.split('.').length === 3) {
        return value
      }
    }
    
    return null
  }, [])

  // Dashboard actions
  const navigateToDashboard = useCallback(async (dashboardId: string) => {
    try {
      const url = dashboardService.navigateToDashboard(dashboardId)
      
      // Get the ID token to include in navigation
      let finalUrl = url
      if (isAuthenticated) {
        try {
          const idToken = getLogtoIdToken()
          if (idToken) {
            const separator = url.includes('?') ? '&' : '?'
            finalUrl = `${url}${separator}token=${idToken}`
          }
        } catch (tokenErr) {
          console.warn('Could not get ID token for navigation:', tokenErr)
        }
      }
      
      window.location.href = finalUrl
    } catch (err) {
      console.error('Navigation failed:', err)
      setError(err instanceof Error ? err : new Error('Navigation failed'))
    }
  }, [isAuthenticated, getLogtoIdToken])

  const openDashboardInNewTab = useCallback(async (dashboardId: string) => {
    try {
      const url = dashboardService.navigateToDashboard(dashboardId)
      
      // Get the ID token to include in navigation
      let finalUrl = url
      if (isAuthenticated) {
        try {
          const idToken = getLogtoIdToken()
          if (idToken) {
            const separator = url.includes('?') ? '&' : '?'
            finalUrl = `${url}${separator}token=${idToken}`
          }
        } catch (tokenErr) {
          console.warn('Could not get ID token for new tab:', tokenErr)
        }
      }
      
      window.open(finalUrl, '_blank', 'noopener,noreferrer')
    } catch (err) {
      console.error('Failed to open dashboard in new tab:', err)
      setError(err instanceof Error ? err : new Error('Failed to open dashboard'))
    }
  }, [isAuthenticated])

  const toggleFavorite = useCallback((dashboardId: string) => {
    if (dashboardService.isFavorite(dashboardId)) {
      dashboardService.removeFromFavorites(dashboardId)
    } else {
      dashboardService.addToFavorites(dashboardId)
    }
    // Force re-render by updating a state that depends on favorites
    setFilters(prev => ({ ...prev }))
  }, [])

  const isFavorite = useCallback((dashboardId: string) => {
    return dashboardService.isFavorite(dashboardId)
  }, [])

  const isRecent = useCallback((dashboardId: string) => {
    return recentDashboards.some(dashboard => dashboard.id === dashboardId)
  }, [recentDashboards])

  // Filter management
  const updateFilters = useCallback((newFilters: DashboardFilters) => {
    setFilters(newFilters)
  }, [])

  const clearFilters = useCallback(() => {
    setFilters({})
  }, [])

  // Dashboard lookup
  const getDashboard = useCallback((dashboardId: string) => {
    return allDashboards.find(dashboard => dashboard.id === dashboardId)
  }, [allDashboards])

  const getDashboardAccess = useCallback((dashboardId: string) => {
    const dashboard = getDashboard(dashboardId)
    if (!dashboard) return null
    
    return {
      dashboard,
      hasAccess: true,
      missingRoles: []
    }
  }, [getDashboard])

  // Refresh dashboards from API
  const refreshDashboards = useCallback(async () => {
    if (!isAuthenticated) return
    
    try {
      setIsLoading(true)
      setError(null)
      await dashboardService.refreshDashboards()
      const dashboards = await dashboardService.getAllDashboards()
      setAllDashboards(dashboards)
    } catch (err) {
      console.error('Failed to refresh dashboards:', err)
      setError(err instanceof Error ? err : new Error('Failed to refresh dashboards'))
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated])

  return {
    // Data
    allDashboards,
    filteredDashboards,
    dashboardsWithAccess,
    accessibleDashboards: allDashboards, // All returned dashboards are accessible
    favoriteDashboards,
    recentDashboards,
    dashboardStats,
    
    // Filter state
    filters,
    filterOptions,
    searchSuggestions,
    
    // Loading state
    isLoading,
    error,
    
    // Actions
    navigateToDashboard,
    openDashboardInNewTab,
    toggleFavorite,
    updateFilters,
    clearFilters,
    refreshDashboards,
    
    // Utilities
    isFavorite,
    isRecent,
    getDashboard,
    getDashboardAccess
  }
} 